// src/pages/FavoritePage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { VStack, useToast } from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AudioCard from "../../components/cards/AudioCard";
import CustomLoader from "../../components/placeholders/CustomLoader";
import {
  CARD_SPACING,
  DEFAULT_WIDTH,
  PAGE_PADDING,
} from "../../helpers/constant";
import SearchBar from "../../components/UI/SearchBar";
import { useRecordings } from "../../context/RecordingContext";
import { extractApiErrorMessage, formatDate } from "../../helpers/utils";
import EmptyState from "../../components/placeholders/EmptyState";
import { BookmarkSimpleIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

const FavoritePage: React.FC = () => {
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) {
      navigate(`/search?q=${encodeURIComponent(q)}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const {
    favorites,
    isLoading,
    hasInitialized,
    setHasInitialized,
    hasLoadedFavorites,
    listFavorites,
    removeFavorite,
    getRecordingFiles,
  } = useRecordings();

  useEffect(() => {
    if (hasInitialized.favorites) {
      return;
    }

    let isMounted = true;

    if (hasLoadedFavorites) {
      setHasInitialized((prev) => ({ ...prev, favorites: true }));
      return;
    }

    listFavorites()
      .catch((error) => {
        toast({
          title: t("pages.favorites.loadErrorTitle"),
          description: extractApiErrorMessage(
            error,
            String(t("common.tryAgain")),
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      })
      .finally(() => {
        if (isMounted) {
          setHasInitialized((prev) => ({ ...prev, favorites: true }));
        }
      });

    return () => {
      isMounted = false;
    };
  }, [
    hasInitialized.favorites,
    hasLoadedFavorites,
    listFavorites,
    setHasInitialized,
    t,
    toast,
  ]);

  const displayedFavorites = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return favorites;
    return favorites.filter((recording) => {
      const searchableText =
        `${recording.title} ${recording.preacherName}`.toLowerCase();
      return searchableText.includes(normalizedQuery);
    });
  }, [favorites, searchQuery]);

  const hasNoData = favorites.length === 0;
  const hasNoSearchResults = displayedFavorites.length === 0 && !hasNoData;

  return (
    <VStack w={DEFAULT_WIDTH} px={PAGE_PADDING} mx="auto" align="start">
      <SearchBar
        value={searchQuery}
        onSearch={setSearchQuery}
        placeholder={String(t("pages.favorites.searchPlaceholder"))}
        mode="local"
      />
      {isLoading || !hasInitialized.favorites ? (
        <CustomLoader />
      ) : hasNoData ? (
        <EmptyState
          icon={BookmarkSimpleIcon}
          title={t("pages.favorites.emptyTitle")}
          description={t("pages.favorites.emptyDescription")}
          mt={7}
        />
      ) : hasNoSearchResults ? (
        <EmptyState
          icon={BookmarkSimpleIcon}
          title={t("pages.search.noResultsTitle")}
          description={t("pages.search.noResultsDescription")}
          mt={7}
        />
      ) : (
        <VStack spacing={CARD_SPACING} w="full" mt={7}>
          {displayedFavorites.map((recording) => (
            <AudioCard
              key={recording.recordingID}
              recordingID={recording.recordingID}
              title={recording.title}
              preacherName={recording.preacherName}
              date={formatDate(
                recording.date,
                i18n.language.startsWith("fr") ? "fr" : "en",
              )}
              hasRecording={Boolean(recording.recordingS3Key)}
              hasSummary={Boolean(recording.summaryS3Key)}
              youtubeLink={recording.youtubeLink}
              saved
              onRemoveFavorite={removeFavorite}
              onRequestFiles={(recordingID) => getRecordingFiles(recordingID)}
              sharePath="/search"
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
};

export default FavoritePage;
